/**
 * Editor for one settings category: a row of `SettingControl`s over the
 * category's non-hidden entries, dirty-tracking per key, with a Save → diff →
 * confirm → sequential PUT flow.
 *
 * Wiring:
 *   - A SettingRow (server-derived: value / source / editable / change / store /
 *     secret) is matched to each registry SettingEntry by key. The server row is
 *     authoritative for `editable` — we trust it over the registry's `dashboard`
 *     field.
 *   - Read-only entries still render their control, disabled, with a provenance
 *     line `store · change` so the operator can see WHERE the value lives and
 *     HOW it changes (PR / operator / etc.).
 *   - `canWrite` (from useAuth) gates the whole editor: a read-only user sees
 *     every control disabled and no Save button.
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useMemo, useRef, useState } from 'react';
import SettingControl from './SettingControl';
import DiffConfirm, { type DiffRow } from './DiffConfirm';
import type { Category, SettingEntry } from '../lib/registry';
import { useAuth } from '../lib/auth';
import { usePutSetting, type SettingRow } from '../lib/queries';
import { ApiError } from '../lib/api';

export interface CategoryEditorProps {
  category: Category;
  /** All server rows, keyed by entry key downstream. */
  rows: SettingRow[];
  onBack: () => void;
}

/** Typing the new mode is required to flip the shadow/live master switch. */
const EXECUTION_MODE_KEY = 'org.execution_mode';

export default function CategoryEditor({ category, rows, onBack }: CategoryEditorProps) {
  const { canWrite } = useAuth();
  const putSetting = usePutSetting();

  const rowByKey = useMemo(() => {
    const m = new Map<string, SettingRow>();
    for (const r of rows) m.set(r.key, r);
    return m;
  }, [rows]);

  // Visible entries: never hidden, and never secret. The server already strips
  // secret values, but we filter here too so a secret can never even render.
  const visible = useMemo(
    () => category.entries.filter((e) => e.dashboard !== 'hidden' && !e.secret),
    [category.entries],
  );

  // Dirty map: key → the pending value EXACTLY as SettingControl emitted it.
  //
  // CONTRACT (Task-4 review): SettingControl's anti-clobber resync gate compares
  // the incoming `value` against its last-emitted value with `Object.is` (===
  // reference identity). So we MUST store the emitted value verbatim — no
  // cloning, spreading, normalizing, or re-parsing — and feed that same
  // reference straight back as the control's `value`. Any copy here would break
  // reference identity and clobber the in-progress edit (e.g. a list's trailing
  // newline, a JSON textarea's cursor position).
  const [dirty, setDirty] = useState<Map<string, unknown>>(new Map());
  // Per-key API error surfaced inline after a failed PUT (ApiError.message).
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [showConfirm, setShowConfirm] = useState(false);
  // Synchronous re-entrancy guard for the Confirm flow: `putSetting.isPending`
  // is still false at the first click and the modal stays open until the whole
  // sequential PUT loop finishes, so a fast double-click would otherwise launch
  // a second concurrent loop. The `submitting` STATE drives button disabling /
  // UX, but it's captured in the render closure — two clicks in the same tick
  // both observe the stale `false` and each fire a full PUT batch. The
  // re-entrancy DECISION must therefore be made on a ref, whose `.current` is
  // mutated synchronously and seen by the second click immediately. Flipped true
  // at the TOP of onConfirm (before any await) and reset in the finally.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  function setValue(key: string, next: unknown) {
    setDirty((prev) => {
      const m = new Map(prev);
      // Store `next` by reference — see CONTRACT above. Do not copy `next`.
      m.set(key, next);
      return m;
    });
    // Re-editing a key clears its stale inline error from a prior failed PUT;
    // otherwise the red message lingers until the next save. No-op (and map
    // identity preserved) when the key has no error.
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const m = new Map(prev);
      m.delete(key);
      return m;
    });
  }

  /** Current displayed value for an entry: the dirty edit if present, else the
   *  server row's value. Returns the dirty reference unchanged so SettingControl
   *  sees the same identity it emitted (anti-clobber contract). */
  function currentValue(entry: SettingEntry): unknown {
    if (dirty.has(entry.key)) return dirty.get(entry.key);
    return rowByKey.get(entry.key)?.value;
  }

  const dirtyEntries = visible.filter((e) => dirty.has(e.key));

  const diffRows: DiffRow[] = dirtyEntries.map((e) => ({
    key: e.key,
    oldValue: rowByKey.get(e.key)?.value,
    newValue: dirty.get(e.key),
  }));

  // The execution-mode master switch requires typing the new mode to confirm.
  const execDirty = dirtyEntries.find((e) => e.key === EXECUTION_MODE_KEY);
  const requireText =
    execDirty && typeof dirty.get(EXECUTION_MODE_KEY) === 'string'
      ? (dirty.get(EXECUTION_MODE_KEY) as string)
      : undefined;

  async function onConfirm() {
    // Re-entrancy guard FIRST, before any await: a second click while the loop
    // is still in flight is a no-op rather than a duplicate PUT batch. The
    // DECISION is ref-based (synchronous, immune to stale-closure double-clicks);
    // the state mirror is set right after purely for button-disabling/UX.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // Sequential PUTs so per-key errors map cleanly and a mid-batch failure
      // doesn't leave ambiguous ordering. Successful keys drop out of the dirty
      // map; failures keep their pending value and surface inline.
      const nextDirty = new Map(dirty);
      const nextErrors = new Map<string, string>();
      for (const e of dirtyEntries) {
        const value = dirty.get(e.key);
        try {
          // NOTE: org.risk_overrides gets an extra server-side field-semantics
          // check (writes.ts validateRiskOverrides: key whitelist + per-field
          // ranges) beyond clientValidate. We deliberately do NOT port it client
          // side — a rejection comes back as a 400 and surfaces inline here.
          await putSetting.mutateAsync({ key: e.key, value });
          nextDirty.delete(e.key);
        } catch (err) {
          const msg =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Write failed';
          nextErrors.set(e.key, msg);
        }
      }
      setDirty(nextDirty);
      setErrors(nextErrors);
      setShowConfirm(false);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <section className="category-editor" aria-label={category.title}>
      <header className="category-editor-header">
        <button type="button" className="btn btn-back" onClick={onBack}>
          ← Back
        </button>
        <h2>{category.title}</h2>
      </header>

      <div className="category-editor-rows">
        {visible.map((entry) => {
          const row = rowByKey.get(entry.key);
          // Server row is authoritative for editability; fall back to the
          // registry's `dashboard` field only when no row exists.
          const editable = row ? row.editable : entry.dashboard === 'editable';
          const disabled = !editable || !canWrite;
          const err = errors.get(entry.key);
          return (
            <div className="category-editor-row" key={entry.key} data-key={entry.key}>
              <div className="setting-meta">
                <span className="setting-title">{entry.title}</span>
                {entry.description && (
                  <span className="setting-desc">{entry.description}</span>
                )}
              </div>
              <SettingControl
                entry={entry}
                value={currentValue(entry)}
                onChange={(next) => setValue(entry.key, next)}
                disabled={disabled}
              />
              {!editable && (
                <p className="setting-provenance">
                  {(row?.store ?? entry.store)} · {(row?.change ?? entry.change)}
                </p>
              )}
              {err && (
                <p className="error api-error" role="alert" data-key={entry.key}>
                  {err}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {canWrite && (
        <div className="category-editor-actions">
          <button
            type="button"
            className="btn btn-save"
            disabled={dirtyEntries.length === 0 || putSetting.isPending}
            onClick={() => setShowConfirm(true)}
          >
            Save{dirtyEntries.length > 0 ? ` (${dirtyEntries.length})` : ''}
          </button>
        </div>
      )}

      {showConfirm && (
        <DiffConfirm
          rows={diffRows}
          requireText={requireText}
          busy={submitting || putSetting.isPending}
          onCancel={() => setShowConfirm(false)}
          onConfirm={onConfirm}
        />
      )}
    </section>
  );
}
